/**
 * 사용자 편집 전용 페이지 — 권한 / 속성 중심.
 *
 * 4 주요 섹션:
 *   1. 그룹 (수동 할당)       — user_role_assignments
 *   2. 그룹 제외              — user_role_exclusions
 *   3. Effective Skill (출처)  — 직접 + 수동 그룹 + attribute 룰 매칭 합집합
 *   4. 사용자 속성            — user_attribute_values
 *
 * 그 외 (직접 부여 Skill / 데이터소스 admin / 외부 정체성 / API 릴레이 / 비밀번호 초기화) 는
 * "고급" collapsed 섹션. 사용자 추가 (CreateForm) 는 여전히 모달 (`UserFormModal`).
 */
import { ResetPasswordDialog } from '@/components/admin/ResetPasswordDialog';
import { UserAttributesEditor } from '@/components/admin/UserAttributesEditor';
import {
  EffectiveSkillPreview,
  RoleAssignmentSelector,
  RoleExclusionsEditor,
  SkillGrantSelector,
} from '@/components/admin/UserFormModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import type {
  AdminUser,
  ToolVisibility,
  UpdateUserRequest,
  UserAttributesRecord,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ChevronDown, ChevronRight, KeyRound, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface Props {
  userId: string;
}

export function AdminUserDetail({ userId }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user: me } = useAuth();

  // 사용자 단건 fetch. 404 = 진짜 존재하지 않음 (재시도 불필요), 그 외 에러는 별도 메시지.
  const userQuery = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => api.adminUserGet(userId),
    retry: (_count, err) => !(err instanceof ApiError && err.status === 404),
  });
  const user: AdminUser | undefined = userQuery.data;

  if (userQuery.isLoading) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      </PageShell>
    );
  }
  if (!user) {
    const err = userQuery.error;
    const msg =
      err instanceof ApiError && err.status === 404
        ? '사용자를 찾을 수 없습니다.'
        : err instanceof ApiError
          ? err.message
          : '사용자를 불러오지 못했습니다.';
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-destructive">{msg}</p>
      </PageShell>
    );
  }

  const isMe = me?.id === user.id;

  return (
    <PageShell
      header={
        <>
          <BackLink />
          <PageHeader
            title={user.displayName}
            description={`${user.email}${user.role === 'admin' ? ' · 관리자' : ''}${user.disabledAt ? ' · 비활성' : ''}`}
          />
        </>
      }
    >
      <div className="space-y-5">
        {user.role === 'admin' ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-300">
            관리자 사용자입니다. 모든 활성 Skill 을 자동으로 가지며 그룹 부여 대상이 아닙니다. 권한
            관련 항목은 일반 사용자에게만 적용됩니다.
          </div>
        ) : null}

        <RolesSection user={user} />
        <ExclusionsSection user={user} />
        <EffectiveSection user={user} />
        <AttributesSection user={user} />
        <AdvancedSection
          user={user}
          isMe={isMe}
          onDeleted={() => {
            qc.invalidateQueries({ queryKey: ['admin', 'users'] });
            navigate({ to: '/admin/users' });
          }}
        />
      </div>
    </PageShell>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/users"
      className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" /> 사용자 목록
    </Link>
  );
}

// ─────────────────────────── 1. 그룹 (수동 할당) ───────────────────────────

function RolesSection({ user }: { user: AdminUser }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const userRolesQuery = useQuery({
    queryKey: ['admin', 'users', user.id, 'roles'],
    queryFn: () => api.adminUserGetRoles(user.id),
    enabled: user.role !== 'admin',
  });
  const allRolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: api.adminRolesList,
    enabled: user.role !== 'admin',
  });

  const [draft, setDraft] = useState<string[] | null>(null);
  const current = userRolesQuery.data?.roles.map((r) => r.id) ?? [];
  const value = draft ?? current;
  const dirty = draft !== null && !arrayEq(draft, current);

  const mutation = useMutation({
    mutationFn: () => api.adminUserSetRoles(user.id, { roleIds: value }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users', user.id, 'roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', user.id, 'effective-skills'] });
      setDraft(null);
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : '저장에 실패했습니다.'),
  });

  return (
    <Section
      title="그룹 (수동 할당)"
      description="부서·룰과 무관하게 명시적으로 부여한 그룹. 부서가 다른 사람을 특정 그룹에 임시 합류시킬 때 사용."
      action={
        dirty ? (
          <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중…' : '저장'}
          </Button>
        ) : null
      }
    >
      {error ? (
        <div className="mb-2 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>
      ) : null}
      <RoleAssignmentSelector
        userRole={user.role}
        roles={allRolesQuery.data?.roles ?? []}
        value={value}
        onChange={setDraft}
        isLoading={allRolesQuery.isLoading || userRolesQuery.isLoading}
      />
    </Section>
  );
}

// ─────────────────────────── 2. 그룹 제외 ───────────────────────────

function ExclusionsSection({ user }: { user: AdminUser }) {
  const allRolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: api.adminRolesList,
    enabled: user.role !== 'admin',
  });
  return (
    <Section
      title="그룹 제외"
      description="룰로 자동 부여된 그룹 중 이 사용자만 차단할 때. 직접 부여 Skill 은 영향 받지 않음."
    >
      {user.role === 'admin' ? (
        <div className="rounded-md border border-border-muted bg-surface-muted px-3 py-2 text-sm text-foreground-muted">
          관리자에겐 적용되지 않습니다.
        </div>
      ) : (
        <RoleExclusionsEditor userId={user.id} roles={allRolesQuery.data?.roles ?? []} />
      )}
    </Section>
  );
}

// ─────────────────────────── 3. Effective Skill ───────────────────────────

function EffectiveSection({ user }: { user: AdminUser }) {
  return (
    <Section
      title="Effective Skill (출처)"
      description="그룹 ∪ 직접 부여로 이 사용자에게 노출되는 Skill 의 출처 미리보기. 뱃지: [직접] / [group] / [auto] / [전체]. 위 섹션 변경 후 자동 갱신."
    >
      {user.role === 'admin' ? (
        <div className="rounded-md border border-border-muted bg-surface-muted px-3 py-2 text-sm text-foreground-muted">
          관리자는 모든 활성 Skill 을 자동으로 가집니다.
        </div>
      ) : (
        <EffectiveSkillPreview userId={user.id} />
      )}
    </Section>
  );
}

// ─────────────────────────── 4. 사용자 속성 ───────────────────────────

function AttributesSection({ user }: { user: AdminUser }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<UserAttributesRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const value = draft ?? user.attributes;
  const dirty = draft !== null && !attrEq(draft, user.attributes);

  const mutation = useMutation({
    mutationFn: () => api.adminUpdateUser(user.id, { attributes: value }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', user.id, 'effective-skills'] });
      setDraft(null);
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : '속성 저장에 실패했습니다.'),
  });

  return (
    <Section
      title="사용자 속성"
      description="회사 차원 속성 스키마는 [권한 > 사용자 속성 정의] 에서 관리. 여기서는 이 사용자의 값만 입력."
      action={
        dirty ? (
          <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중…' : '저장'}
          </Button>
        ) : null
      }
    >
      {error ? (
        <div className="mb-2 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>
      ) : null}
      <UserAttributesEditor value={value} onChange={setDraft} />
    </Section>
  );
}

// ─────────────────────────── 5. 고급 (collapsed) ───────────────────────────

function AdvancedSection({
  user,
  isMe,
  onDeleted,
}: {
  user: AdminUser;
  isMe: boolean;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const [displayName, setDisplayName] = useState(user.displayName);
  const [allowedSkillIds, setAllowedSkillIds] = useState<string[]>(user.allowedSkillIds);
  const [apiRelayEnabled, setApiRelayEnabled] = useState(user.apiRelayEnabled);
  const [override, setOverride] = useState<'' | ToolVisibility>(user.toolVisibilityOverride ?? '');
  const [error, setError] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(null);

  const updateMutation = useMutation({
    mutationFn: (body: UpdateUserRequest) => api.adminUpdateUser(user.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : '저장에 실패했습니다.'),
  });

  const resetMutation = useMutation({
    mutationFn: () => api.adminResetPassword(user.id),
    onSuccess: (r) => setResetResult({ email: user.email, password: r.temporaryPassword }),
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : '비밀번호 초기화에 실패했습니다.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.adminDeleteUser(user.id),
    onSuccess: onDeleted,
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : '삭제에 실패했습니다.'),
  });

  return (
    <section className="rounded-md border border-border bg-surface">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium hover:bg-surface-muted/40"
      >
        {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        고급
        <span className="text-foreground-muted text-xs font-normal">
          (이름·직접 Skill·API 릴레이·툴 노출·비밀번호·삭제)
        </span>
      </button>
      {open ? (
        <div className="border-t border-border p-4 space-y-4">
          {error ? (
            <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>
          ) : null}

          <Sub label="표시 이름">
            <div className="flex gap-2">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="h-10 flex-1 rounded-md border border-border bg-surface px-3 text-sm"
              />
              <Button
                type="button"
                onClick={() => updateMutation.mutate({ displayName })}
                disabled={updateMutation.isPending || displayName === user.displayName}
              >
                저장
              </Button>
            </div>
          </Sub>

          {user.role !== 'admin' ? (
            <Sub label="직접 부여 Skill" hint="그룹과 별개로 이 사용자에게만 부여할 Skill">
              <SkillGrantSelector
                role={user.role}
                value={allowedSkillIds}
                onChange={setAllowedSkillIds}
              />
              <div className="mt-2 flex justify-end">
                <Button
                  type="button"
                  onClick={() => updateMutation.mutate({ allowedSkillIds })}
                  disabled={
                    updateMutation.isPending || arrayEq(allowedSkillIds, user.allowedSkillIds)
                  }
                >
                  저장
                </Button>
              </div>
            </Sub>
          ) : null}

          <Sub
            label="LLM API 릴레이"
            hint="외부에서 OpenAI 호환 키 호출 허용 (글로벌 토글 OFF 면 무효)."
          >
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={apiRelayEnabled}
                onChange={(e) => setApiRelayEnabled(e.target.checked)}
              />
              <span>{apiRelayEnabled ? '허용' : '비활성'}</span>
              {apiRelayEnabled !== user.apiRelayEnabled ? (
                <Button
                  type="button"
                  onClick={() => updateMutation.mutate({ apiRelayEnabled })}
                  disabled={updateMutation.isPending}
                >
                  저장
                </Button>
              ) : null}
            </label>
          </Sub>

          <Sub label="Skill 툴 노출 (디버그)">
            <div className="flex gap-2">
              <select
                value={override}
                onChange={(e) => setOverride(e.target.value as '' | ToolVisibility)}
                className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
              >
                <option value="">Skill 기본값 따름</option>
                <option value="summary">요약 (summary)</option>
                <option value="hidden">숨김 (hidden)</option>
                <option value="detailed">상세 (detailed)</option>
              </select>
              {(override === '' ? null : override) !== user.toolVisibilityOverride ? (
                <Button
                  type="button"
                  onClick={() =>
                    updateMutation.mutate({
                      toolVisibilityOverride: override === '' ? null : override,
                    })
                  }
                  disabled={updateMutation.isPending}
                >
                  저장
                </Button>
              ) : null}
            </div>
          </Sub>

          <Sub label="비밀번호 초기화" hint="임시 비밀번호 생성 후 다이얼로그로 표시.">
            <Button
              type="button"
              variant="ghost"
              onClick={() => resetMutation.mutate()}
              disabled={resetMutation.isPending}
            >
              <KeyRound className="size-4" /> 비밀번호 초기화
            </Button>
          </Sub>

          <Sub label="계정 삭제" hint="채팅 세션·artifact 도 cascade 로 함께 삭제됩니다.">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (isMe) return;
                if (window.confirm(`${user.email} 계정을 삭제하시겠습니까?`)) {
                  deleteMutation.mutate();
                }
              }}
              disabled={isMe || deleteMutation.isPending}
              className={cn(isMe ? '' : 'hover:text-destructive')}
            >
              <Trash2 className="size-4" /> 삭제
            </Button>
          </Sub>
        </div>
      ) : null}

      {resetResult ? (
        <ResetPasswordDialog
          open
          email={resetResult.email}
          temporaryPassword={resetResult.password}
          onClose={() => setResetResult(null)}
        />
      ) : null}
    </section>
  );
}

// ─────────────────────────── 공통 유틸 ───────────────────────────

function Section({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-border bg-surface p-4">
      <header className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-xs text-foreground-muted">{description}</p>
          ) : null}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function Sub({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-sm font-medium">{label}</div>
      {hint ? <div className="mb-1.5 text-xs text-foreground-muted">{hint}</div> : null}
      <div className={hint ? '' : 'mt-1.5'}>{children}</div>
    </div>
  );
}

function arrayEq(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((x) => setA.has(x));
}

function attrEq(a: UserAttributesRecord, b: UserAttributesRecord): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => a[k] === b[k]);
}
